import { defineAsyncComponent } from 'vue';
import Build from '@views/info/Build';
import { rolesConstants } from '@utils/constants';
import OidcCallback from '@views/oidc/OidcCallback.vue';

const TheCustomersSearch = () =>
    import(
        /* webpackChunkName: "theCustomerSearch" */ '@/views/customerSupport/TheCustomersSearch'
    );
const CustomerSearchTabCustomer = () =>
    import(
        /* webpackChunkName: "theCustomerSearchTabCustomer" */ '@/views/customerSupport/TheCustomersSearch/components/CustomerSearchTabCustomer'
    );
const CustomerSearchTabLoyaltyTransactions = () =>
    import(
        /* webpackChunkName: "theCustomerSearchTabLoyalty" */ '@/views/customerSupport/TheCustomersSearch/components/CustomerSearchTabLoyaltyTransactions'
    );
const CustomerSearchTabOrders = () =>
    import(
        /* webpackChunkName: "theCustomerSearchTabOrders" */ '@/views/customerSupport/TheCustomersSearch/components/CustomerSearchTabOrders'
    );
const TheDashboard = () =>
    import(/* webpackChunkName: "theDashboard" */ '@/views/dashboard/TheDashboard');
const TheNotFound = () => import(/* webpackChunkName: "theNotFound" */ '@views/static/TheNotFound');
const TheForbidden = () =>
    import(/* webpackChunkName: "theForbidden" */ '@views/static/TheForbidden');
const TheReportCustomersActivities = () =>
    import(
        /* webpackChunkName: "theReportCustomersActivities" */ '@views/reports/TheReportCustomersActivities'
    );
const TheReportOrders = () =>
    import(/* webpackChunkName: "theReportOrders" */ '@views/reports/TheReportOrders');
const TheReportEventOrderDetails = () =>
    import(
        /* webpackChunkName: "theReportEventOrderDetails" */ '@views/reports/TheReportEventOrderDetails'
    );
const TheReportProductsEvents = () =>
    import(
        /* webpackChunkName: "TheReportProductsEvents" */ '@views/reports/TheReportProductsEvents'
    );
const TheReportFinanceOrders = () =>
    import(
        /* webpackChunkName: "TheReportFinanceOrders" */ '@views/reports/TheReportFinanceOrders'
    );
const TheReportFinanceTMHotelReconciliation = () =>
    import(
        /* webpackChunkName: "TheReportFinanceOrders" */ '@views/reports/TheReportFinanceTMHotelReconciliation'
    );
const TheReportFinanceTMHotelReconciliationHotelOnly = () =>
    import(
        /* webpackChunkName: "TheReportFinanceHotelOnlyOrders" */ '@views/reports/TheReportFinanceTMHotelReconciliationHotelOnly'
    );
const TmReconciliationDynamic = () =>
    import(
        /* webpackChunkName: "HotelReconciliationDynamic" */ '@modules/reports/tmReconciliationDynamic/components/ReportPanel'
    );
const BmtCancellations = () =>
    import(/* webpackChunkName: "BmtCancellations" */ '@views/reports/TheReportBmtCancellations');
const TheReportFinanceCommission = () =>
    import(
        /* webpackChunkName: "TheReportFinanceCommission" */ '@views/reports/TheReportFinanceCommission'
    );
const TheReportFinanceCommissionDetails = () =>
    import(
        /* webpackChunkName: "TheReportFinanceCommissionDetails" */ '@views/reports/finance/TheCommissionDetails'
    );
const TheHotelPriceRules = () =>
    import(
        /* webpackChunkName: "TheHotelPriceRules" */ '@views/hotelManagement/TheHotelPriceRules'
    );
const TheReportProductsHotels = () =>
    import(
        /* webpackChunkName: "TheReportProductsHotels" */ '@views/reports/TheReportProductsHotels'
    );
const HotelDetailsTabRooms = () =>
    import(
        /* webpackChunkName: "HotelDetailsTabRooms" */ '@views/reports/TheReportProductsHotels/HotelDetailsTabRooms'
    );
const HotelDetailsTabImages = () =>
    import(
        /* webpackChunkName: "HotelDetailsTabImages" */ '@views/reports/TheReportProductsHotels/HotelDetailsTabImages'
    );
const HotelDetailsTabOverview = () =>
    import(
        /* webpackChunkName: "HotelDetailsTabOverview" */ '@views/reports/TheReportProductsHotels/HotelDetailsTabOverview'
    );
const OrderDetailsModal = defineAsyncComponent(
    () => import(/* webpackChunkName: "OrderDetailsModal" */ '@components/modals/OrderDetailsModal')
);
const TheOfferTokens = () =>
    import(/* webpackChunkName: "OfferTokens" */ '@views/marketing/TheOfferTokens');
const TheAddOfferToken = () =>
    import(/* webpackChunkName: "AddOfferToken" */ '@views/marketing/TheAddOfferToken');
const TheEditOfferToken = () =>
    import(/* webpackChunkName: "EditOfferToken" */ '@views/marketing/TheEditOfferToken');

const routes = [
    {
        path: '/:pathMatch(.*)*',
        name: 'pageNotFound',
        component: TheNotFound,
        meta: {
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: false,
        },
    },
    {
        path: '/',
        name: 'dashboard',
        component: TheDashboard,
        meta: {
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: false,
        },
    },
    {
        path: '/theHotelPriceRules',
        name: 'theHotelPriceRules',
        component: TheHotelPriceRules,
        meta: {
            breadcrumb: [
                '_common:navigationTree.hotelManagement.group',
                '_common:navigationTree.hotelManagement.children.priceRules',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: false,
        },
    },

    {
        path: '/customerSupport/search',
        component: TheCustomersSearch,
        meta: {
            breadcrumb: [
                '_common:navigationTree.customerSupport.group',
                '_common:navigationTree.customerSupport.children.customerSearch',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
        },
        children: [
            {
                path: '',
                name: 'customers.search',
                component: CustomerSearchTabCustomer,
                meta: {
                    breadcrumb: [
                        '_common:navigationTree.customerSupport.group',
                        '_common:navigationTree.customerSupport.children.customerSearch',
                    ],
                    layout: 'default',
                    isPublic: false,
                    checkAccessControlsBeforeEnter: true,
                    roles: [
                        rolesConstants.OIDC_ROLES.CUSTOMER_VIEWER,
                        rolesConstants.OIDC_ROLES.GST_CUSTOMER_SUPPORT,
                    ],
                },
            },
            {
                name: 'customers.search.orders',
                path: 'orders',
                component: CustomerSearchTabOrders,
                meta: {
                    breadcrumb: [
                        '_common:navigationTree.customerSupport.group',
                        '_common:navigationTree.customerSupport.children.customerSearch',
                    ],
                    layout: 'default',
                    isPublic: false,
                    checkAccessControlsBeforeEnter: true,
                    roles: [
                        rolesConstants.OIDC_ROLES.CUSTOMER_VIEWER,
                        rolesConstants.OIDC_ROLES.GST_CUSTOMER_SUPPORT,
                        rolesConstants.OIDC_ROLES.GST_FINANCE_USER,
                    ],
                },
            },
            {
                name: 'customers.search.loyaltyTransactions',
                path: 'loyaltyTransactions',
                component: CustomerSearchTabLoyaltyTransactions,
                meta: {
                    breadcrumb: [
                        '_common:navigationTree.customerSupport.group',
                        '_common:navigationTree.customerSupport.children.customerSearch',
                    ],
                    layout: 'default',
                    isPublic: false,
                    checkAccessControlsBeforeEnter: true,
                    roles: [
                        rolesConstants.OIDC_ROLES.CUSTOMER_VIEWER,
                        rolesConstants.OIDC_ROLES.GST_CUSTOMER_SUPPORT,
                    ],
                },
            },
        ],
    },
    {
        path: '/reports/customers/activities',
        name: 'reports.customers.activities',
        component: TheReportCustomersActivities,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.customers.parent',
                '_common:navigationTree.reports.children.customers.children.activitiesSummary',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.CUSTOMER_REPORTS],
        },
    },
    {
        path: '/reports/orders/summary',
        name: 'reports.orders.summary',
        component: TheReportOrders,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.orders.parent',
                '_common:navigationTree.reports.children.orders.children.ordersSummary',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.SALES_REPORTS],
        },
    },
    {
        path: '/reports/orders/event/orderDetails/',
        name: 'reports.orders.eventOrderDetails',
        component: TheReportEventOrderDetails,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.orders.parent',
                '_common:navigationTree.reports.children.orders.children.eventOrderDetails',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.SALES_REPORTS],
        },
    },
    {
        path: '/reports/products/events',
        name: 'reports.products.events',
        component: TheReportProductsEvents,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.products.parent',
                '_common:navigationTree.reports.children.products.children.topSellingEvents',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.PRODUCT_VIEWER],
        },
    },
    {
        path: '/reports/products/hotel-details',
        component: TheReportProductsHotels,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.products.parent',
                '_common:navigationTree.reports.children.products.children.hotelDetails',
            ],
            layout: 'default',
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.PRODUCT_VIEWER],
        },
        children: [
            {
                path: '',
                name: 'reports.products.hotelDetails.roomsOrders',
                component: HotelDetailsTabRooms,
                meta: {
                    breadcrumb: [
                        '_common:navigationTree.reports.group',
                        '_common:navigationTree.reports.children.products.parent',
                        '_common:navigationTree.reports.children.products.children.hotelDetails',
                    ],
                    layout: 'default',
                    isPublic: false,
                    checkAccessControlsBeforeEnter: true,
                    roles: [rolesConstants.OIDC_ROLES.PRODUCT_VIEWER],
                    preserveScrollPosition: true,
                },
            },
            {
                name: 'reports.products.hotelDetails.images',
                path: 'images',
                component: HotelDetailsTabImages,
                meta: {
                    breadcrumb: [
                        '_common:navigationTree.reports.group',
                        '_common:navigationTree.reports.children.products.parent',
                        '_common:navigationTree.reports.children.products.children.hotelDetails',
                    ],
                    layout: 'default',
                    isPublic: false,
                    checkAccessControlsBeforeEnter: true,
                    roles: [rolesConstants.OIDC_ROLES.PRODUCT_VIEWER],
                    preserveScrollPosition: true,
                },
            },
            {
                name: 'reports.products.hotelDetails.overview',
                path: 'overview',
                component: HotelDetailsTabOverview,
                meta: {
                    breadcrumb: [
                        '_common:navigationTree.reports.group',
                        '_common:navigationTree.reports.children.products.parent',
                        '_common:navigationTree.reports.children.products.children.hotelDetails',
                    ],
                    layout: 'default',
                    isPublic: false,
                    checkAccessControlsBeforeEnter: true,
                    roles: [rolesConstants.OIDC_ROLES.PRODUCT_VIEWER],
                    preserveScrollPosition: true,
                },
            },
        ],
    },
    {
        path: '/reports/finance/summary',
        name: 'reports.finance.summary',
        component: TheReportFinanceOrders,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.finance.parent',
                '_common:navigationTree.reports.children.finance.children.ordersSummary',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.FINANCE_REPORTS],
        },
    },
    {
        path: '/reports/finance/hotelReconciliation',
        name: 'reports.finance.hotelReconciliation',
        component: TheReportFinanceTMHotelReconciliation,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.finance.parent',
                '_common:navigationTree.reports.children.finance.children.hotelReconciliation.breadcrumbTitle',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.FINANCE_REPORTS],
        },
    },
    {
        path: '/reports/finance/hotelReconciliationHotelOnly',
        name: 'reports.finance.hotelReconciliationHotelOnly',
        component: TheReportFinanceTMHotelReconciliationHotelOnly,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.finance.parent',
                '_common:navigationTree.reports.children.finance.children.hotelReconciliationHotelOnly.breadcrumbTitle',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.FINANCE_REPORTS],
        },
    },
    {
        path: '/reports/finance/tmReconciliationDynamic',
        name: 'reports.finance.tmReconciliationDynamic',
        component: TmReconciliationDynamic,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.finance.parent',
                '_common:navigationTree.reports.children.finance.children.tmReconciliationDynamic.breadcrumbTitle',
            ],
            layout: 'default',
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.FINANCE_REPORTS],
        },
    },
    {
        path: '/reports/customerSupport/bmtCancellations',
        name: 'reports.customerSupport.bmtCancellations',
        component: BmtCancellations,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.customerSupport.parent',
                '_common:navigationTree.reports.children.customerSupport.children.bmtCancellations.breadcrumbTitle',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.GST_CUSTOMER_SUPPORT],
        },
    },
    {
        name: 'orderDetailsModal',
        path: '/orderDetailsModal/:orderId',
        meta: {
            type: 'modal',
            modalConfig: {
                component: OrderDetailsModal,
                props: {},
                config: {},
                configDesktop: {
                    'no-click-animation': true,
                    scrollable: true,
                    fullscreen: false,
                    'hide-overlay': false,
                    'max-width': 1754,
                    'content-class': 'gst-v-dialog',
                },
                configMobile: {
                    'content-class': 'v-dialog__height-90 gst-v-dialog',
                    scrollable: true,
                    fullscreen: false,
                    'hide-overlay': false,
                    transition: 'dialog-bottom-transition',
                },
            },
            isPublic: true,
        },
    },
    {
        path: '/reports/finance/commissionSummary',
        name: 'reports.finance.commissionSummary',
        component: TheReportFinanceCommission,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.finance.parent',
                '_common:navigationTree.reports.children.finance.children.commissionSummary',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.FINANCE_REPORTS],
        },
    },
    {
        path: '/reports/finance/commissionDetails',
        name: 'reports.finance.commissionDetails',
        component: TheReportFinanceCommissionDetails,
        meta: {
            breadcrumb: [
                '_common:navigationTree.reports.group',
                '_common:navigationTree.reports.children.finance.parent',
                '_common:navigationTree.reports.children.finance.children.commissionDetails',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.FINANCE_REPORTS],
        },
    },
    {
        path: '/offerTokens',
        name: 'offerTokens',
        component: TheOfferTokens,
        meta: {
            breadcrumb: [
                '_common:navigationTree.marketing.group',
                '_common:navigationTree.marketing.children.offerTokens.parent',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [
                rolesConstants.OIDC_ROLES.PRODUCT_VIEWER,
                rolesConstants.OIDC_ROLES.PRODUCT_MANAGER,
            ],
        },
    },
    {
        path: '/offerTokens/addOfferToken',
        name: 'offerTokens/addOfferToken',
        component: TheAddOfferToken,
        meta: {
            breadcrumb: [
                '_common:navigationTree.marketing.group',
                '_common:navigationTree.marketing.children.offerTokens.parent',
                '_common:navigationTree.marketing.children.offerTokens.children.addOfferToken',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.PRODUCT_MANAGER],
        },
    },
    {
        path: '/offerTokens/:offerToken/edit',
        name: 'offerTokens/editOfferToken',
        component: TheEditOfferToken,
        meta: {
            breadcrumb: [
                '_common:navigationTree.marketing.group',
                '_common:navigationTree.marketing.children.offerTokens.parent',
                '_common:navigationTree.marketing.children.offerTokens.children.editOfferToken',
            ],
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: true,
            roles: [rolesConstants.OIDC_ROLES.PRODUCT_MANAGER],
        },
    },

    {
        path: '/404',
        name: 'theNotFound',
        component: TheNotFound,
        meta: {
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: false,
        },
    },
    {
        path: '/403',
        name: 'theForbidden',
        component: TheForbidden,
        meta: {
            layout: 'default',
            isPublic: true,
            checkAccessControlsBeforeEnter: false,
        },
    },
    {
        path: '/info/build',
        component: Build,
        name: 'info.build',
        meta: {
            layout: 'default',
            isPublic: false,
            checkAccessControlsBeforeEnter: false,
        },
    },
    {
        path: '/oidc-callback', // Needs to match redirect_uri in you oidcSettings
        name: 'oidcCallback',
        component: OidcCallback,
        meta: {
            layout: 'loading',
            isOidcCallback: true,
            isPublic: true,
            loginPage: true,
            checkAccessControlsBeforeEnter: false,
        },
    },
];

export default routes;
